import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { Container, Row, Col } from "react-bootstrap";
import { ArrowRightCircle } from "react-bootstrap-icons";

import headerImg from "../assets/img/WebKbanner2.png";
import projImg1 from "../assets/img/Products03.jpg";

const embeddedFeatures = [
  "Auto-exposure & auto-gain",
  "Non-uniformity correction (NUC)",
  "Auto white balance",
  "High dynamic range (HDR)",
  "Denoising, contrast, and edge enhancement",
  "Digital image stabilisation",
];

const keySpecifications = [
  "1920 × 1080 native resolution (1/1.2″ optical format)",
  "Global-shutter Sony Pregius IMX174 CMOS",
  "Up to 60 fps via 3G-SDI or HD-SDI",
  "Auto-exposure, auto-gain, NUC, auto white balance",
  "Up to 102 dB dynamic range with embedded HDR",
  "Denoising, contrast & edge enhancement, gamma correction",
  "Digital image stabilisation",
  "Rugged, compact housing for harsh environments",
];

const OpalCam = () => {
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash.replace("#", "");

    if (hash) {
      const element = document.getElementById(hash);

      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <main className="opal-page">

      {/* HERO */}
      <section className="opal-product-hero" id="opal">
        <Container>
          <Row className="align-items-center g-5">

            <Col xs={12} lg={6}>
              <div className="opal-product-hero__content">
                <span className="opal-product-eyebrow">
                  VISIBLE LIGHT IMAGING
                </span>

                <h1>
                  <span>Opal-1920S</span>
                  Visible Light Camera
                </h1>

                <p>
                  The <strong>Opal-1920S</strong> is a compact, 1080p camera
                  engineered for industrial imaging, machine vision, security,
                  metrology, and laboratory workflows.
                </p>

                <div
                  className="opal-product-highlights"
                  aria-label="Opal-1920S key specifications"
                >
                  <div>
                    <strong>1920 × 1080</strong>
                    <span>Native Resolution</span>
                  </div>

                  <div>
                    <strong>Up to 60 fps</strong>
                    <span>3G-SDI or HD-SDI</span>
                  </div>

                  <div>
                    <strong>Up to 102 dB</strong>
                    <span>Embedded HDR</span>
                  </div>
                </div>

                <HashLink
                  smooth
                  className="opal-product-button"
                  to="/#connect"
                >
                  Contact Our Team
                  <ArrowRightCircle aria-hidden="true" />
                </HashLink>
              </div>
            </Col>

            <Col xs={12} lg={6}>
              <div className="opal-product-hero__image">
                <img
                  src={headerImg}
                  alt="Kristal Fotonik Opal-1920S Visible Light Camera"
                />
              </div>
            </Col>

          </Row>
        </Container>
      </section>

      {/* PRODUCT OVERVIEW */}
      <section className="opal-product-overview">
        <Container>
          <Row className="align-items-stretch g-5">

            <Col xs={12} lg={7}>
              <div className="opal-product-overview__content">
                <span className="opal-section-eyebrow">
                  PRODUCT OVERVIEW
                </span>

                <h2>Opal-1920S Visible Light Camera</h2>

                <p>
                  It uses a <strong>global-shutter CMOS</strong> architecture
                  to capture crisp motion even in uncontrolled lighting
                  conditions, while the sealed housing provides resilience
                  against shock, vibration, and extreme temperatures.
                </p>

                <p>
                  The camera features{" "}
                  <strong>
                    switch-selectable 3G-SDI (SMPTE 424-1)
                  </strong>{" "}
                  and <strong>HD-SDI (SMPTE 292M)</strong> outputs via a single
                  coax, allowing flexible bandwidth selection. Embedded
                  firmware handles:
                </p>

                <p className="opal-product-overview__result">
                  These features ensure top-tier image quality and resolution{" "}
                  <strong>without requiring external processing</strong>.
                </p>
              </div>
            </Col>

            <Col xs={12} lg={5}>
              <aside
                className="opal-product-features"
                aria-label="Opal-1920S embedded features"
              >
                <h3>Embedded image processing</h3>

                <ul>
                  {embeddedFeatures.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
              </aside>
            </Col>

          </Row>
        </Container>
      </section>

      {/* SENSOR */}
      <section className="opal-sensor-section">
        <Container>
          <Row className="align-items-center g-5">

            <Col xs={12} lg={5}>
              <div className="opal-sensor-image">
                <img
                  src={projImg1}
                  alt="Sony Pregius IMX174 image sensor"
                />
              </div>
            </Col>

            <Col xs={12} lg={7}>
              <div className="opal-sensor-content">
                <span className="opal-section-eyebrow">
                  IMAGE SENSOR
                </span>

                <h2>Powered by Sony Pregius IMX174 Sensor</h2>

                <p>
                  The camera leverages the{" "}
                  <strong>Sony Pregius IMX174</strong> sensor to offer excellent
                  sensitivity and a high dynamic range, eliminating smear and
                  blooming even under specular highlights.
                </p>
              </div>
            </Col>

          </Row>
        </Container>
      </section>

      {/* KEY SPECIFICATIONS */}
      <section
        className="opal-specifications"
        aria-labelledby="opal-specifications-title"
      >
        <Container>
          <div className="opal-specifications__heading">
            <span className="opal-section-eyebrow">
              TECHNICAL DATA
            </span>

            <h2 id="opal-specifications-title">
              Key Specifications
            </h2>
          </div>

          <div className="opal-specifications__grid">
            {keySpecifications.map((specification, index) => (
              <div className="opal-specification-card" key={specification}>
                <span>
                  {String(index + 1).padStart(2, "0")}
                </span>

                <p>{specification}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* APPLICATIONS */}
      <section className="opal-applications">
        <Container>
          <div className="opal-applications__content">
            <span className="opal-section-eyebrow">
              BUILT FOR DEMANDING APPLICATIONS
            </span>

            <p>
              With its unique combination of{" "}
              <strong>durability, image fidelity</strong>, and{" "}
              <strong>user-configurable features</strong>, the Opal-1920S is
              ready for use in demanding inspection lines, mobile surveillance
              systems, medical devices, and research instruments—anywhere{" "}
              <strong>high-quality HD video over coax</strong> is required.
            </p>
          </div>
        </Container>
      </section>

      {/* CONTACT */}
      <section className="opal-product-cta">
        <Container>
          <div>
            <span>PRODUCT INTEGRATION</span>

            <h2>Discuss your imaging requirements.</h2>

            <p>
              Contact our team for product details, integration support and
              availability.
            </p>
          </div>

          <HashLink
            smooth
            className="opal-product-button opal-product-button--light"
            to="/#connect"
          >
            Get In Touch
            <ArrowRightCircle aria-hidden="true" />
          </HashLink>
        </Container>
      </section>

    </main>
  );
};

export default OpalCam;